import { normalizeRoleKey, expandRoleAliases } from "./roleHelpers.mjs";

const NCRM_ONBOARDING_ROUTE_ROLES = Object.freeze(["admin", "miniadmin", "crm-admin", "crm-operator"]);

const addRoleAliases = (roleSet, roleValue) => {
  const normalized = normalizeRoleKey(roleValue);
  if (!normalized) return;
  expandRoleAliases(normalized).forEach((alias) => roleSet.add(alias));
};

const readStoredRoleSet = () => {
  const roleSet = new Set();
  const storage = typeof globalThis === "object" ? globalThis.localStorage : null;
  if (!storage) return roleSet;

  addRoleAliases(roleSet, storage.getItem("user-role"));
  addRoleAliases(roleSet, storage.getItem("user-primary-role"));

  try {
    const raw = JSON.parse(storage.getItem("user-roles") || "[]");
    if (Array.isArray(raw)) {
      raw.forEach((entry) => {
        if (!entry) return;
        if (typeof entry === "string") {
          addRoleAliases(roleSet, entry);
          return;
        }
        addRoleAliases(roleSet, entry.key || entry.role || entry.name);
      });
    }
  } catch (_) {
    // ignore storage parse failures and fallback to currently known roles
  }

  return roleSet;
};

const canAccessOnboardingActivitySource = (options = {}) => {
  if (typeof options.allowOnboardingRoute === "boolean") {
    return options.allowOnboardingRoute;
  }

  const roleSet = new Set();
  const roleKeys = Array.isArray(options.roleKeys)
    ? options.roleKeys
    : options.roleKeys instanceof Set
      ? Array.from(options.roleKeys)
      : options.roleKeys
        ? [options.roleKeys]
        : [];
  roleKeys.forEach((roleValue) => addRoleAliases(roleSet, roleValue));
  addRoleAliases(roleSet, options.legacyRole);

  if (!roleSet.size) {
    readStoredRoleSet().forEach((roleValue) => roleSet.add(roleValue));
  }

  if (!roleSet.size) return true;
  return NCRM_ONBOARDING_ROUTE_ROLES.some((role) => roleSet.has(role));
};

export function resolveNcrmContactDetailsRoute(activeType, recordId) {
  const id = String(recordId ?? "").trim();
  if (!id) return null;

  if (activeType === "company") {
    return {
      name: "NcrmCompanyDetails",
      params: { id },
    };
  }

  if (activeType === "person") {
    return {
      name: "NcrmPersonDetails",
      params: { id },
    };
  }

  return null;
}

export function resolveNcrmActivitySourceRoute(activity = {}, options = {}) {
  const onboardingId = String(activity.onboarding_id ?? activity.onboardingId ?? "").trim();
  if (onboardingId && canAccessOnboardingActivitySource(options)) {
    return {
      name: "NcrmOnboardingDetails",
      params: { onboardingId },
    };
  }

  const entityType = String(activity.entity_type ?? activity.entityType ?? "").trim().toLowerCase();
  const entityId = String(activity.entity_id ?? activity.entityId ?? "").trim();

  if (entityType === "deal" && entityId) {
    return {
      name: "NcrmDealDetails",
      params: { id: entityId },
      query: { tab: "activity" },
    };
  }

  if (entityType === "contract" && entityId) {
    return {
      name: "NcrmContractDetails",
      params: { contractId: entityId },
      query: { tab: "activity" },
    };
  }

  const originEntityType = String(
    activity.origin_entity_type ?? activity.originEntityType ?? ""
  )
    .trim()
    .toLowerCase();
  const originEntityId = String(activity.origin_entity_id ?? activity.originEntityId ?? "").trim();
  if (originEntityType === "lead_entry" && originEntityId) {
    const originBoardId = String(activity.origin_board_id ?? activity.originBoardId ?? "").trim();
    const query = { tab: "activity" };
    if (originBoardId) query.boardId = originBoardId;
    return {
      name: "NcrmLeadDetails",
      params: { id: originEntityId },
      query,
    };
  }

  if (!entityId) return null;

  if (entityType === "company") {
    return {
      name: "NcrmCompanyDetails",
      params: { id: entityId },
      query: { tab: "activity" },
    };
  }

  if (entityType === "person") {
    return {
      name: "NcrmPersonDetails",
      params: { id: entityId },
      query: { tab: "activity" },
    };
  }

  return null;
}
